import type React from 'react';
import { Box, Card, Heading, Icon, Paragraph, Table } from '@zillow/constellation-10';
import { IconFileFilled } from '@zillow/constellation-icons';
import { isBefore } from 'date-fns';
import { coerceToDate } from '@common/paymentsUtils/dateUtils';
import { PaymentReportingStatus } from '../../Dashboard/modules/PaymentReportingStatus';
import type { PaymentReportHistoryInterface, RenterTransactionProps } from '../../../models/Types';

interface RentReportingHistoryProps {
  transactions: Array<RenterTransactionProps>;
  paymentReportHistory: PaymentReportHistoryInterface;
}

const formatScheduleMonth = (scheduleMonth: string): string => {
  const [year, month] = scheduleMonth.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleString('default', { month: 'long', year: 'numeric' });
};

const formatDateRange = (startDate: string, endDate: string): string => {
  const start = formatScheduleMonth(startDate);
  const end = formatScheduleMonth(endDate);
  return `${start} - ${end}`;
};

const renderTransactionTitle = (transaction: RenterTransactionProps): string => {
  if (transaction.paymentCategory === 'rentPayment') {
    return formatScheduleMonth(transaction.scheduleMonth);
  }
  return transaction.dateCreated;
};

interface Transaction {
  title: string;
  transactions: Array<RenterTransactionProps>;
  isGrouped: boolean;
}

const RentReportingHistory: React.FC<RentReportingHistoryProps> = ({ transactions, paymentReportHistory }) => {
  const transactionsFiltered = transactions.filter((transaction) => transaction.paymentCategory === 'rentPayment');
  if (transactionsFiltered.length === 0) {
    return null;
  }

  const { optInDate } = paymentReportHistory;

  // Sort all transactions by scheduleMonth in descending order (most recent first)
  const sortedTransactions = transactionsFiltered.sort((a, b) => b.scheduleMonth.localeCompare(a.scheduleMonth));

  const allTransactions: Array<Transaction> = [];
  const beforeOptInTransactions: Array<RenterTransactionProps> = [];

  // Process transactions in sorted order
  for (const transaction of sortedTransactions) {
    if (optInDate && isBefore(coerceToDate(transaction.scheduleMonth) as Date, coerceToDate(optInDate) as Date)) {
      beforeOptInTransactions.push(transaction);
    } else {
      // Add individual transactions after opt-in date immediately
      allTransactions.push({
        title: renderTransactionTitle(transaction),
        transactions: [transaction],
        isGrouped: false,
      });
    }
  }

  // Add grouped transactions before opt-in date (last)
  if (beforeOptInTransactions.length > 0) {
    // beforeOptInTransactions is already in descending order, so reverse for chronological order
    const chronologicalBeforeOptIn = beforeOptInTransactions.reverse();
    const firstMonth = chronologicalBeforeOptIn[0].scheduleMonth;
    const lastMonth = chronologicalBeforeOptIn[chronologicalBeforeOptIn.length - 1].scheduleMonth;

    allTransactions.push({
      title: formatDateRange(firstMonth, lastMonth),
      transactions: chronologicalBeforeOptIn,
      isGrouped: true,
    });
  }

  return (
    <Box css={{ marginTop: 'token(spacing.lg) !important' }}>
      <Card.Root elevated={false}>
        <Card.Header css={{ marginBottom: '0 !important' }}>
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: '100%',
              padding: 'token(spacing.xs) token(spacing.xs) token(spacing.xs) token(spacing.sm) !important',
            }}
          >
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Icon render={<IconFileFilled />} css={{ marginRight: 'token(spacing.xs)' }} />
              <Heading level={6}>Report history</Heading>
            </Box>
          </Box>
        </Card.Header>
        <Card.Body>
          <Box css={{ padding: '0 token(spacing.sm) 0 token(spacing.sm) !important' }}>
            <Table.Root appearance="horizontal" aria-label="Payment history" size="md">
              <Table.Body>
                {allTransactions.map((groupedTransaction, index) => (
                  <Table.Row
                    key={index}
                    css={{
                      '&:last-child': {
                        '& td': {
                          borderBottom: '0 !important',
                        },
                      },
                    }}
                  >
                    <Table.Cell css={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                      <Paragraph>{groupedTransaction.title}</Paragraph>
                    </Table.Cell>
                    <Table.Cell css={{ verticalAlign: 'top' }}>
                      <PaymentReportingStatus
                        transaction={groupedTransaction.transactions[0]}
                        paymentReportHistory={paymentReportHistory}
                        currentScheduleMonth={{
                          scheduleMonth: groupedTransaction.transactions[0].scheduleMonth,
                          dueDate: groupedTransaction.transactions[0].dueDate,
                        }}
                        showVisibleText={true}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        </Card.Body>
      </Card.Root>
    </Box>
  );
};

export default RentReportingHistory;
